import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  Receipt,
  Settings,
  Briefcase,
  Plus,
  ChevronDown,
  ClipboardList,
  Send,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAccountantRole } from '@/hooks/useRole';
import { useAccountantPendingCount } from '@/hooks/useAccountantPendingCount';
import { Badge } from '@/components/ui/badge';

const documentsItems = [
  { href: '/invoices', label: 'nav.invoices', icon: FileText },
  { href: '/quotes', label: 'nav.quotes', icon: ClipboardList },
  { href: '/expenses', label: 'nav.expenses', icon: Receipt },
];

const registryItems = [
  { href: '/clients', label: 'nav.clients', icon: Users },
  { href: '/suppliers', label: 'nav.suppliers', icon: Briefcase },
  { href: '/products', label: 'nav.products', icon: Package },
];

const quickActions = [
  { route: '/invoices', label: 'nav.newInvoice', icon: FileText },
  { route: '/quotes', label: 'nav.newQuote', icon: ClipboardList },
  { route: '/expenses', label: 'nav.newExpense', icon: Receipt },
  { route: '/clients', label: 'nav.newClient', icon: Users },
  { route: '/suppliers', label: 'nav.newSupplier', icon: Briefcase },
  { route: '/products', label: 'nav.newProduct', icon: Package },
];

export function Sidebar() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { isAccountant, isLoading } = useAccountantRole();
  const { receivedPendingCount, sentPendingCount } = useAccountantPendingCount();

  const handleQuickAction = (route: string) => {
    // Navigate to /new page for creating new records
    navigate(`${route}/new`);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
      'hover:bg-accent hover:text-accent-foreground',
      isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
    );

  if (isLoading) return null;

  if (isAccountant) {
    return (
      <aside className="hidden md:flex md:flex-col w-64 border-r bg-background h-[calc(100vh-4rem)] sticky top-16">
        <nav className="flex flex-col h-full p-4">
          <div className="space-y-1">
            <NavLink to="/accountant" end className={navLinkClass}>
              <LayoutDashboard className="h-4 w-4" />
              <span>{t('nav.accountantOverview')}</span>
            </NavLink>
            <NavLink to="/accountant/clients" className={navLinkClass}>
              <Users className="h-4 w-4" />
              <span>{t('nav.accountantClients')}</span>
            </NavLink>
            <NavLink to="/accountant/pending" className={navLinkClass}>
              <Send className="h-4 w-4" />
              <span className="flex-1">{t('nav.accountantInvitations')}</span>
              {receivedPendingCount > 0 ? (
                <Badge className="ml-auto text-xs px-1.5 py-0.5 h-auto bg-amber-500 hover:bg-amber-500 text-white">
                  {receivedPendingCount}
                </Badge>
              ) : sentPendingCount > 0 ? (
                <Badge className="ml-auto text-xs px-1.5 py-0.5 h-auto bg-blue-500 hover:bg-blue-500 text-white">
                  {sentPendingCount}
                </Badge>
              ) : null}
            </NavLink>
          </div>
          <div className="mt-auto pt-4">
            <NavLink to="/settings" className={navLinkClass}>
              <Settings className="h-4 w-4" />
              <span>{t('nav.settings')}</span>
            </NavLink>
          </div>
        </nav>
      </aside>
    );
  }

  return (
    <aside className="hidden md:flex md:flex-col w-64 border-r bg-background h-[calc(100vh-4rem)] sticky top-16">
      <nav className="flex flex-col h-full p-4">
        {/* New button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="w-full mb-4 justify-between">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span>{t('nav.newItem')}</span>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.route}
                  onClick={() => handleQuickAction(action.route)}
                  className="cursor-pointer"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  <span>{t(action.label)}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Dashboard */}
        <NavLink to="/dashboard" className={navLinkClass}>
          <LayoutDashboard className="h-4 w-4" />
          <span>{t('nav.dashboard')}</span>
        </NavLink>

        {/* Documents section */}
        <div className="mt-6">
          <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('nav.documents')}
          </p>
          <div className="space-y-1">
            {documentsItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.href} to={item.href} className={navLinkClass}>
                  <Icon className="h-4 w-4" />
                  <span>{t(item.label)}</span>
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Registry section */}
        <div className="mt-6">
          <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('nav.registry')}
          </p>
          <div className="space-y-1">
            {registryItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.href} to={item.href} className={navLinkClass}>
                  <Icon className="h-4 w-4" />
                  <span>{t(item.label)}</span>
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Settings at bottom */}
        <div className="mt-auto pt-4">
          <NavLink to="/settings" className={navLinkClass}>
            <Settings className="h-4 w-4" />
            <span>{t('nav.settings')}</span>
          </NavLink>
        </div>
      </nav>
    </aside>
  );
}
